import {
  createContext,
  useContext,
  useEffect,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { formatCurrency, isCurrencyAmount, parseCurrencyAmount } from '@/lib/format';
import productApi from '@/lib/productApi';
import { useQuoteStore } from '@/store/quoteStore';
import type { RateView } from '@/types/product';
import { groupRates } from '@/components/products/rateGroups';

type RateCatalog = { rates: RateView[]; status: 'loading' | 'ready' | 'error' };
const RateCatalogContext = createContext<RateCatalog | null>(null);

function useRateCatalog(enabled = true): RateCatalog {
  const portalId = useQuoteStore((state) => state.currentQuote?.portalId);
  const [catalog, setCatalog] = useState<RateCatalog>({ rates: [], status: 'loading' });
  useEffect(() => {
    if (!enabled) return;
    let active = true;
    setCatalog({ rates: [], status: 'loading' });
    productApi
      .rates(portalId)
      .then((rates) => {
        if (active)
          setCatalog({
            rates: rates.filter((rate) => !rate.archivedAt && rate.currentCents !== null),
            status: 'ready',
          });
      })
      .catch(() => {
        if (active) setCatalog({ rates: [], status: 'error' });
      });
    return () => {
      active = false;
    };
  }, [enabled, portalId]);
  return catalog;
}

/** A whole overview shares one catalog request across its editable rows. */
export function InvestmentRateCatalogProvider({ children }: { children: ReactNode }) {
  const catalog = useRateCatalog();
  return <RateCatalogContext.Provider value={catalog}>{children}</RateCatalogContext.Provider>;
}

export default function InvestmentRateSelect({
  rateKey,
  amount,
  currency,
  onSelect,
  onCustomChange,
  onKeyDown,
}: {
  rateKey?: string;
  amount: string;
  currency: string;
  onSelect: (rateKey: string, amountCents?: number) => void;
  onCustomChange: (value: string) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
}) {
  const sharedCatalog = useContext(RateCatalogContext);
  const ownCatalog = useRateCatalog(!sharedCatalog);
  const { rates, status } = sharedCatalog ?? ownCatalog;
  const [draft, setDraft] = useState(amount);
  useEffect(() => setDraft(amount), [amount]);
  const value = rateKey ?? (amount.trim() ? 'custom' : '');
  const selected = rates.find((rate) => rate.key === value);
  const invalid =
    Boolean(draft.trim()) && (!isCurrencyAmount(draft) || parseCurrencyAmount(draft) < 0);
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1.5">
      <span className="text-[12px] font-semibold text-slate-700">Uurtarief</span>
      <Select
        value={value}
        onValueChange={(key) => {
          const rate = rates.find((rate) => rate.key === key);
          onSelect(key, rate?.currentCents ?? undefined);
        }}
      >
        <SelectTrigger className="h-9 w-full text-xs" aria-label="Uurtarief kiezen">
          <SelectValue placeholder={status === 'loading' ? 'Tarieven laden…' : 'Kies een tarief'}>
            {value
              ? value === 'custom'
                ? 'Eigen tarief'
                : `${selected?.name ?? 'Eerder gekozen tarief'} · ${amount || '—'}`
              : undefined}
          </SelectValue>
        </SelectTrigger>
        <SelectContent data-investment-inspector className="max-w-[calc(100vw-2rem)]">
          {value && value !== 'custom' && !selected && (
            <SelectItem value={value} disabled>
              Eerder gekozen tarief · {amount}
            </SelectItem>
          )}
          {groupRates(rates).map((group) => (
            <SelectGroup key={group.name}>
              <SelectLabel>{group.name}</SelectLabel>
              {group.rates.map((rate) => (
                <SelectItem key={rate.id} value={rate.key}>
                  {rate.name} · {formatCurrency(rate.currentCents! / 100, currency)}
                </SelectItem>
              ))}
            </SelectGroup>
          ))}
          <SelectItem value="custom">Eigen tarief</SelectItem>
        </SelectContent>
      </Select>
      {value === 'custom' && (
        <input
          aria-label="Eigen uurtarief"
          aria-invalid={invalid || undefined}
          inputMode="decimal"
          value={draft}
          placeholder="0,00"
          className="h-9 w-full rounded-md border border-gray-200 bg-white px-2.5 text-[13px] outline-none focus:border-slate-400"
          onChange={(event) => {
            const next = event.target.value;
            setDraft(next);
            if (!next.trim() || (isCurrencyAmount(next) && parseCurrencyAmount(next) >= 0))
              onCustomChange(next);
          }}
          onBlur={() => {
            if (invalid) {
              setDraft(amount);
              return;
            }
            const next = draft.trim() ? formatCurrency(parseCurrencyAmount(draft), currency) : '';
            if (next !== amount) onCustomChange(next);
          }}
          onKeyDown={(event) => {
            onKeyDown?.(event);
            if (!event.defaultPrevented && event.key === 'Enter') event.currentTarget.blur();
          }}
        />
      )}
      {invalid && value === 'custom' && (
        <span role="alert" className="text-xs text-red-600">
          Vul een geldig tarief van 0 of hoger in.
        </span>
      )}
      {status === 'error' && (
        <span role="status" className="text-xs text-slate-500">
          Tarieven laden mislukt. Je kunt een eigen tarief invullen.
        </span>
      )}
    </div>
  );
}
